<template>
  <div class="dui-credit-card-form">
    <div
      v-show="!requireKey || !!publishableKey"
      class="grid-2 grid-middle grid-gutters-lg"
    >
      <div class="grid-item">
        <label class="label-sm">Card Number</label>
        <div
          :id="`card-element-${ _uid }`"
          class="card-element"
        />
      </div>

      <div class="grid-item">
        <label class="label-sm">Name on card</label>
        <input
          v-model="fullName"
          class="input-full"
          placeholder="Enter your full name"
          type="text"
          @input="cardNameUpdated()"
        >
      </div>
    </div>

    <div
      v-if="requireKey && !publishableKey"
      class="loading-container text-center"
    >
      <DuiLoadingIndicator size="lg" />
    </div>

    <div
      ref="cardErrors"
      class="card-errors"
      role="alert"
    />
  </div>
</template>

<script>
const CARD_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontFamily: 'Rubik,Myriad Pro,sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.25,
      '::placeholder': {
        color: '#9b9b9b',
      },
    },
  },
};

export default {
  props: {
    applePayButtonHeight: {
      type: Number,
      default: 40,
    },
    publishableKey: {
      type: String,
      default: null,
    },
    requireKey: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    applePayPaymentRequest: null,
    card: null,
    fullName: null,
    stripe: null,
    stripeElements: null,
  }),
  watch: {
    publishableKey: function() {
      this.initCard();
    },
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
  mounted() {
    this._boundFns = {
      cardChange: this.cardChangePerformed.bind(this),
    };

    this.initCard();
  },
  methods: {
    initCard() {
      if (this.requireKey && !this.publishableKey) {
        return;
      }

      const publishableKey = this.publishableKey || process.env.STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        throw new Error('Stripe key not set.');
      }

      this.stripe = Stripe(publishableKey); // eslint-disable-line new-cap
      this.stripeElements = this.stripe.elements();
      this.card = this.stripeElements.create('card', CARD_OPTIONS);
      this.card.mount(`#card-element-${ this._uid }`);
      this.addEventListeners();
    },

    cardChangePerformed(ev) {
      if (ev.error) {
        this.$refs.cardErrors.innerText = ev.error.message;
        return;
      }

      this.$refs.cardErrors.innerText = '';
    },

    async submitAndGetPaymentIntentId(clientSecret) {
      const { paymentIntent, error } = await this.stripe.handleCardPayment(
        clientSecret,
        this.card,
        {
          payment_method_data: {
            billing_details: {
              name: this.fullName,
            },
          },
        },
      );

      if (error) {
        this.$refs.cardErrors.innerText = error.message;
        return null;
      }

      return paymentIntent.id;
    },

    async setupApplePay(applePayButtonSelector, label, amount) {
      if (!applePayButtonSelector) {
        throw new Error('applePayButtonSelector not set.');
      }

      if (this.applePayPaymentRequest) {
        this.applePayPaymentRequest.update({
          total: {
            amount: amount,
            label: label,
          },
        });

        return;
      }

      this.applePayPaymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        requestPayerEmail: true,
        requestPayerName: true,
        total: {
          amount: amount,
          label: label,
        },
      });

      const prButton = this.stripeElements.create('paymentRequestButton', {
        paymentRequest: this.applePayPaymentRequest,
        style: {
          paymentRequestButton: {
            type: 'default', // | 'donate' | 'buy', // default: 'default'
            theme: 'dark', // | 'light' | 'light-outline', // default: 'dark'
            height: `${ this.applePayButtonHeight }px`, // default: '40px', the width is always '100%'
          },
        },
      });

      const canMakePayment = await this.applePayPaymentRequest.canMakePayment();
      if (canMakePayment && canMakePayment.applePay === true) {
        prButton.mount(applePayButtonSelector);
        this.$emit('apple-pay-available');
      } else {
        document.querySelector(applePayButtonSelector).remove();
        this.$emit('apple-pay-unavailable');
        return;
      }

      this.applePayPaymentRequest.on('paymentmethod', (ev) => {
        ev.complete('success');
        this.$emit('apple-pay-complete', ev.paymentMethod.id);
      });
    },

    // Events
    addEventListeners() {
      this.card.addEventListener('change', this._boundFns.cardChange);
    },

    cardNameUpdated() {
      this.$emit('card-name-updated', this.fullName);
    },

    removeEventListeners() {
      this.card.removeEventListener('change', this._boundFns.cardChange);
    },
    // END Events
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.dui-credit-card-form {
  border-top: $border-width-base solid getColor($grey, fade);
  margin: $_3em 0;
  padding-top: $_3em;

  .loading-container {
    padding: 15px 0;
  }

  .card-element {
    -webkit-appearance: none;
    background-color: getColor($white);
    border: $border-width-base solid getColor($grey, fade);
    border-radius: $border-radius-base;
    box-sizing: border-box;
    color: getColor($black);
    font-size: $font-size-base;
    line-height: 1.25;
    outline: none;
    margin: 0;
    padding: $_1-5em $_2em;

    @include transition((
      border-color,
      color
    ));

    &:last-child {
      margin-right: 0;
    }

    &:focus {
      border-color: getColor($grey, dark);
    }

    &.input-full {
      width: 100%;
    }

    &[disabled] {
      color: getColor($grey, dark);
      pointer-events: none;
      opacity: $disabled-opacity;
    }
  }

  .card-errors {
    color: getColor($red);
    font-weight: $base-weight-bold;
    margin-top: $_1em;
  }
}
</style>
